import { useEffect, useState } from "react";
import { Button, FormControlLabel, RadioGroup, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { TivaRadioButton } from "../../radio-button/radio-button.comp";
import { CitationDisplay } from "../../../common/enums/citation-display.enum";
import {
    CITATION_DISPLAY_DESCRIPTIONS,
    CITATION_DISPLAY_LABELS,
    CITATION_DISPLAY_ORDER
} from "../../../common/utilities/citation-display.util";

export interface VisibilitySaveValues {
    // undefined = leave unchanged; null = inherit provider default; value = override.
    citationDisplay?: CitationDisplay | null;
}

type CitationChoice = "unchanged" | "inherit" | CitationDisplay;

const VisibilityDialog = ({
    open,
    onClose,
    single,
    count,
    initialCitationDisplay,
    onSave
}: {
    open: boolean;
    onClose: () => void;
    single: boolean;
    count: number;
    initialCitationDisplay?: CitationDisplay | null;
    onSave: (values: VisibilitySaveValues) => void;
}) => {
    const initialCitationChoice = (): CitationChoice =>
        single
            ? initialCitationDisplay == null
                ? "inherit"
                : initialCitationDisplay
            : "unchanged";

    const [citationChoice, setCitationChoice] = useState<CitationChoice>(initialCitationChoice());

    // Re-seed when the dialog (re)opens or the target changes.
    useEffect(() => {
        if (open) {
            setCitationChoice(initialCitationChoice());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, single, initialCitationDisplay]);

    const buildValues = (): VisibilitySaveValues => {
        const values: VisibilitySaveValues = {};
        if (citationChoice !== "unchanged") {
            values.citationDisplay = citationChoice === "inherit" ? null : citationChoice;
        }
        return values;
    };

    const values = buildValues();
    const nothingToApply = values.citationDisplay === undefined;

    const handleSave = () => {
        if (nothingToApply) return;
        onSave(values);
    };

    return (
        <ModalTemplate
            open={open}
            width={560}
            handleClose={onClose}
            ariaLabel="Resource visibility"
        >
            <div className="visibility-dialog">
                <div className="visibility-dialog-heading">
                    <div className="visibility-dialog-title">
                        <SvgIcon
                            className="visibility-dialog-title__icon"
                            component={EyeIcon}
                            inheritViewBox
                        />
                        <span className="visibility-dialog-title__text">Resource visibility</span>
                    </div>
                    {!single && (
                        <span className="visibility-dialog-subtitle">
                            {count} file{count === 1 ? "" : "s"}
                        </span>
                    )}
                </div>

                <div className="visibility-dialog-section">
                    <span className="visibility-dialog-section__label">In question responses</span>
                    <RadioGroup
                        className="visibility-dialog-radios"
                        value={citationChoice}
                        onChange={e => setCitationChoice(e.target.value as CitationChoice)}
                    >
                        {!single && (
                            <FormControlLabel
                                className="visibility-dialog-radio"
                                value="unchanged"
                                control={<TivaRadioButton />}
                                label="Leave unchanged"
                            />
                        )}
                        <FormControlLabel
                            className="visibility-dialog-radio"
                            value="inherit"
                            control={<TivaRadioButton />}
                            label="Use source default"
                        />
                        {CITATION_DISPLAY_ORDER.map(mode => (
                            <FormControlLabel
                                key={mode}
                                className="visibility-dialog-radio"
                                value={mode}
                                control={<TivaRadioButton />}
                                label={
                                    <span className="visibility-dialog-option">
                                        <span className="visibility-dialog-option__name">
                                            {CITATION_DISPLAY_LABELS[mode]}
                                        </span>
                                        <span className="visibility-dialog-option__desc">
                                            {CITATION_DISPLAY_DESCRIPTIONS[mode]}
                                        </span>
                                    </span>
                                }
                            />
                        ))}
                    </RadioGroup>
                </div>

                <div className="visibility-dialog-action">
                    <Button className="thirt-button" variant="text" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        className="primary-button visibility-dialog-action__button-margin"
                        variant="text"
                        disabled={nothingToApply}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};

export default VisibilityDialog;
